import { useReducer, useEffect, useCallback } from 'react'
import api, { IHRLocation, IDepartment, IWorkplaceGroup, ISpace, IEmployee, IUsageType } from '@api'
import { uniqueId, last } from 'lodash'
import orderBy from 'lodash/fp/orderBy'
import { useSelection } from '@hooks'
import { ISelection } from '@hooks/useSelection'
import { useShouldReload } from '@hooks/reloadContext'
import { IGridTableCell, GridTableStatusEnum } from '@ui'
import { DateUtil } from '@utils/dateUtil'
import { Order, IBooking } from '@api/types'
import get from 'lodash/get'
import {
  BaseTableFilters,
  Sort,
  UseBookingsTableHook,
  Actions,
  ActionBase,
} from '../../CurrentBookingsTable/types'
import isEmpty from 'lodash/isEmpty'

export interface Filters extends BaseTableFilters {
  locations: any[]
  HRLocations: IHRLocation[]
  departments: IDepartment[]
  WPGs: IWorkplaceGroup[]
  spaces: ISpace[]
  employees: IEmployee[]
  usageTypes: IUsageType[]
}

interface State {
  reqId: string
  status: GridTableStatusEnum
  count: number
  toDate: Date | null
  fromDate: Date | null
  cachedItems: IGridTableCell<IBooking>[]
  items: IGridTableCell<IBooking>[]
  sort: Sort
  filters: BaseTableFilters
}

export interface UseBookingsHook extends UseBookingsTableHook, State {
  sortBy: (column: string) => void
  selection: ISelection
  resetFilters: () => void
  applyFilters: (filters: BaseTableFilters) => void
  updateFilters: (filters: Partial<BaseTableFilters>) => void
}

type SetItems = {
  type: Actions.SET
  items: IGridTableCell<IBooking>[]
}

type LoadItems = {
  type: Actions.LOAD
  reqId: string
  items: IGridTableCell<IBooking>[]
  count: number
  fromDate: Date
  toDate: Date
}

type Action = ActionBase | LoadItems | SetItems

type TOrder = 'asc' | 'desc'

const persistedState: State = {
  reqId: uniqueId('request'),
  status: GridTableStatusEnum.LOADING,
  count: 0,
  items: [],
  toDate: null,
  fromDate: null,
  cachedItems: [],
  sort: { column: 'startDate', order: Order.ASC },
  filters: {
    query: '',
    fromDate: null,
    toDate: null,
  },
}

export default function useBookingsData(): UseBookingsHook {
  const [state, dispatch] = useReducer(reducer, persistedState)
  const selection = useSelection()
  const shouldReload = useShouldReload()

  const resetBookingItems = useCallback(() => {
    dispatch({
      type: Actions.SET,
      items: state.cachedItems,
    })
  }, [state.cachedItems])

  const orderItems = useCallback(
    orderBy<IGridTableCell<IBooking>>((item) => get(item, `data.${state.sort.column}`)),
    [state.sort.column]
  )

  const fetchBookings =
    (shouldClearSelection = true) =>
    () => {
      const reqId = uniqueId('request')

      dispatch({ type: Actions.LOAD_START_WITHOUT_SKELETON, reqId })

      api.booking.current(DateUtil().toISOString()).then((bookings: any[]) => {
        const isAllItemsSelected = selection.hasAllItemsSelected(state.items.length)
        const itemIds: string[] = []

        const items = bookings.map(({ id, ...data }) => {
          if (isAllItemsSelected) {
            itemIds.push(id)
          }

          return { id, data: { id, ...data } }
        })

        //clear selection outside filtered range
        if (shouldClearSelection) {
          selection.removeSelectionsByItems(items)
        }

        // select all items if the select all is checked
        if (isAllItemsSelected && items.length) {
          selection.addAll(itemIds)
        }

        const orderedItemsByStartDate = orderBy(
          (item) => get(item, 'data.startDate'),
          'desc',
          items
        )
        const fromDate = last(orderedItemsByStartDate)?.data?.startDate
        const toDate = orderedItemsByStartDate[0].data.startDate

        dispatch({
          type: Actions.LOAD,
          count: bookings.length,
          items: orderItems(state.sort.order.toLocaleLowerCase() as TOrder, items),
          toDate: DateUtil(toDate).toDate(),
          fromDate: DateUtil(fromDate).toDate(),
          reqId,
        })
      })
    }

  //api request without skeleton and with load all items in state.first
  useEffect(fetchBookings(), [shouldReload.bookings])

  useEffect(() => {
    dispatch({
      type: Actions.SET,
      items: orderItems(state.sort.order.toLocaleLowerCase() as TOrder, state.items),
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.sort])

  // date filter
  useEffect(() => {
    if (!state.filters.fromDate && !state.filters.toDate) {
      resetBookingItems()
      return
    }

    const start = state.filters.fromDate as Date
    const end = state.filters.toDate as Date

    const filteredItems = state.cachedItems.filter(({ data: { space, startDate } }) => {
      const timezone = space.floor.building?.timezone ?? ''
      return DateUtil(startDate).tz(timezone).isInTheRange({
        start,
        end,
      })
    })

    dispatch({
      type: Actions.SET,
      items: filteredItems,
    })
  }, [state.filters.toDate, state.filters.fromDate, resetBookingItems, state.cachedItems])

  // searching
  useEffect(() => {
    if (isEmpty(state.filters.query)) {
      resetBookingItems()
      return
    }

    const filteredItems = state.cachedItems.filter(({ data: { space } }) => {
      const query = state.filters.query
      return (
        space.externalId?.toLowerCase().includes(query) ||
        space.floor.building?.address.toLowerCase().includes(query) ||
        space.floor.building?.city?.toLowerCase().includes(query) ||
        false
      )
    })

    dispatch({
      type: Actions.SET,
      items: filteredItems,
    })
  }, [resetBookingItems, state.cachedItems, state.filters.query])

  return {
    ...state,
    selection,
    sortBy: (column: string) => {
      dispatch({ type: Actions.SORT, column })
    },
    resetFilters: () => {
      dispatch({
        type: Actions.RESET_FILTERS,
      })
    },
    applyFilters: (filters: BaseTableFilters) => {
      dispatch({
        type: Actions.FILTER,
        filters,
      })
    },
    updateFilters: (filters) => {
      dispatch({
        type: Actions.FILTER,
        filters: {
          ...state.filters,
          ...filters,
        },
      })
    },
  }
}

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case Actions.SET:
      return {
        ...state,
        items: action.items,
      }
    case Actions.LOAD_START:
      return {
        ...state,
        reqId: action.reqId,
        status: GridTableStatusEnum.LOADING,
      }
    case Actions.LOAD_START_WITHOUT_SKELETON:
      return {
        ...state,
        reqId: action.reqId,
        status: GridTableStatusEnum.IDLE,
      }

    case Actions.ERROR:
      return action.reqId === state.reqId ? { ...state, status: GridTableStatusEnum.IDLE } : state

    case Actions.LOAD:
      return action.reqId === state.reqId
        ? {
            ...state,
            status: GridTableStatusEnum.IDLE,
            count: action.count,
            cachedItems: [...action.items],
            items: action.items,
            fromDate: action.fromDate,
            toDate: action.toDate,
          }
        : state

    case Actions.SORT:
      return {
        ...state,
        status: GridTableStatusEnum.IDLE,
        sort: {
          column: action.column,
          order:
            state.sort.column === action.column
              ? state.sort.order === Order.ASC
                ? Order.DESC
                : Order.ASC
              : Order.ASC,
        },
      }

    case Actions.RESET_FILTERS:
      return {
        ...state,
        items: [...state.cachedItems],
        filters: {
          query: '',
          fromDate: null,
          toDate: null,
        },
      }

    case Actions.FILTER:
      return {
        ...state,
        filters: action.filters,
      }

    default:
      return state
  }
}
